<template>
  <TsCard
    @clicked="emit('clicked', id)"
    append-class="bg-white flex flex-col h-full rounded text-left"
    no-shadow
    header-class="pb-0"
    footer-class="mt-auto"
  >
    <template #header>
      <TsRow no-gutter append-class="justify-between mb-2">
        <TsTypography variant="sm" append-class="mb-0" weight="semibold">
          {{ props.userName }}
        </TsTypography>
        <TsTypography variant="xs" append-class="text-info flex items-center gap-0.5">
          <TsIcon name="solar:verified-check-outline" size="20" />
          <span>{{useTranslation("verified" , "Geverifieerde aankoop")}}</span>
        </TsTypography>
      </TsRow>
      <TsTypography as="div" variant="sm" append-class="mb-4 flex items-center gap-2">
        <div class="flex items-center">
          <TsRatings :model-value="props.rating" />
          <span>{{ props.rating }}</span>
        </div>
        <div class="h-1 w-1 bg-natural-grey rounded-full"></div>
        <span>{{ dateConversion(props.reviewDate) }}</span>
      </TsTypography>
    </template>
    <template #content>
      <TsTypography variant="h4" weight="semibold">
        {{ props.title }}
      </TsTypography>
      <TsTypography
        variant="sm"
        :append-class="
          twMerge(
            'mb-0 text-idle-black',
            props.images.length > 0 ? 'line-clamp-3' : 'line-clamp-6'
          )
        "
      >
        {{ props.review }}
      </TsTypography>
      <div class="flex items-center gap-2 mt-3">
        <TsImage
          v-for="(image,index) in props.images"
          :key="image"
          :src="image"
          :alt="`reviews-${index}`"
          height="64"
          width="64"
          loading="lazy"
          @click="openImageModal(index)"
        />
      </div>
      <Teleport to="body">
          <TsModal v-model:visible="review_images_modal">
            <template #modal-content="{ modal }">
              <div class="relative">
                <TsIcon
                  @click="modal?.hide()"
                  class="cursor-pointer rounded-md hover:bg-gray-200 absolute top-0 right-0 m-4 z-10"
                  name="bitcoin-icons:cross-outline"
                />
              </div> 
              <div class="relative overflow-hidden w-full">  
                <div class="flex transition-transform duration-300">
                    <TsCarouselV2 
                        append-class="w-[98%] mx-auto lg:px-0 px-0" 
                        :pagination="false"
                        ref="modalCarouselRef"
                    >
                    <TsCarouselSlide
                      v-for="(image, index) in props.images"
                      :key="index"
                      class="flex-[0_0_100%] justify-between items-center flex"
                    >
                    <TsImage       
                        :key="image"
                        :src="image"
                        :alt="`reviews-${index}`"
                        width="400"
                        height="400"
                        class="min-w-[102px] m-auto"
                        loading="lazy"                    
                      />
                    </TsCarouselSlide>
                  </TsCarouselV2>
                </div>
              </div>
            </template>
          </TsModal>
        </Teleport>
    </template>
    <template #footer>
      <div class="flex items-center gap-2">
        <span class="text-sm text-natural-grey">
          {{useTranslation("productRecommendation" , "Beveelt dit product aan")}} : {{ props.isRecommended ? useTranslation("yes" , "Ja") : useTranslation("no" , "Nee") }}
        </span>
        <TsIcon v-if="props.isRecommended" class="text-success" name="lets-icons:check-fill" size="18" />
      </div>
      <TsTypography append-class="p-2 bg-natural-soft-white rounded-sm"  v-if="props.productUrl">Originally posted on <NuxtLinkWithLocale class="text-info underline hover:no-underline" :to="props.productUrl">{{ props.productName }}</NuxtLinkWithLocale></TsTypography>
    </template>
  </TsCard>
</template>

<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { useTranslation } from "~/composables/useTranslation";

const TsCard = defineAsyncComponent(
    () => import('~/components/organisms/TsCard.vue')
);

const TsRatings = defineAsyncComponent(
    () => import('~/components/molecules/TsRatings.vue')
);
const TsCarouselSlide = defineAsyncComponent(
    () => import('~/components/organisms/TsCarouselSlide.vue')
);
const TsCarouselV2 = defineAsyncComponent(
    () => import('~/components/organisms/TsCarouselV2.vue')
);

function dateConversion(isoDateString: string) {
  const date = new Date(isoDateString);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Month is zero-indexed, so add 1
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const emit = defineEmits(["clicked"]);

type Props = {
  userName?: string;
  time?: string;
  title?: string;
  review?: string;
  reviewDate?: string;
  images?: string[];
  rating?: number;
  id?: string;
  isRecommended?: boolean;
  location?: string;
  productName?: string;
  productUrl?: string;
};


const props = withDefaults(defineProps<Props>(), {
  userName: "Rob 001",
  title: "Drill",
  review:
    "Excellent drill. The weight and feel in hand is real good. As a hammer drill it performs very well too. Excellent drill. The weight and feel in hand is real good. As a hammer drill it performs very well too. Excellent drill. The weight and feel in hand is real good. As a hammer drill it performs very well too. Excellent drill. The weight and feel in hand is real good. As a hammer drill it performs very well too. Excellent drill. The weight and feel in hand is real good. As a hammer drill it performs very well too. Excellent drill. The weight and feel in hand is real good. As a hammer drill it performs very well too. Excellent drill. The weight and feel in hand is real good. As a hammer drill it performs very well too.",
  reviewDate: "11 Dec 2023",
  rating: 4,
  isRecommended: true,
  location: "Nederland",
  images: () => ["/images/review-product-1.png", "/images/review-product-2.png"],
});

const review_images_modal = ref(false);
const selected_image_index = ref(0);

const modalCarouselRef = ref(null);

function openImageModal(index: number) {
  selected_image_index.value = index;
  review_images_modal.value = true;
}
watch(review_images_modal, (isVisible) => {
  if (isVisible) {
    setTimeout(() => {
      if (modalCarouselRef.value?.emblaApi) {
        modalCarouselRef.value.emblaApi.scrollTo(selected_image_index.value);
      }
    }, 100);
  }
});
</script>
